/* eslint-disable no-nested-ternary */
import React, {
  Children,
  cloneElement,
  memo,
  ReactElement,
  ReactNode,
  isValidElement,
  CSSProperties,
} from 'react'

import styled from '@emotion/styled'
import { isPlainObject } from 'is-plain-object'

import { useMemoizedCall, useUniqId } from '@surglogs-app/hooks'
import { Info } from '@surglogs-app/icons'
import {
  Box,
  FlexBox,
  Icon,
  Label,
  LabelProps,
  StyleProps,
} from '@surglogs-app/ui'
import { WrapInToolTip } from '@surglogs-app/ui.tooltip'

import { useTranslation } from '../services'

import { FormFieldValidationText } from './FormFieldValidationText'

const FormFieldContainer = styled(Box)`
  position: relative;
`

export const FormFieldLabel = styled(Label)`
  padding-bottom: 0;
`
enum ValidationLabelType {
  WARNING = 'warning',
  ERROR = 'error',
  INFO = 'info',
}

export interface FormFieldProps extends Omit<StyleProps, 'color'> {
  children: (({ id }: { id: string }) => ReactNode) | ReactNode
  label?: string | ReactNode | Omit<LabelProps, 'ref'>
  labelTooltip?: string | ReactNode
  error?: boolean | string | ReactElement
  info?: boolean | string | ReactElement
  warning?: boolean | string | ReactElement
  style?: CSSProperties
  className?: string
}

const renderLabel = (
  id: string,
  label: FormFieldProps['label'],
  optionalText: string,
) => {
  if (label) {
    if (isPlainObject(label) && !isValidElement(label)) {
      return (
        <FormFieldLabel
          {...{ htmlFor: id, optionalText }}
          {...(label as object)}
        />
      )
    }

    return (
      <FormFieldLabel {...{ htmlFor: id, optionalText }}>
        {label as string}
      </FormFieldLabel>
    )
  }

  return null
}

const getVariant = (type: ValidationLabelType) => {
  if (type === ValidationLabelType.WARNING) {
    return 'warning'
  }

  if (type === ValidationLabelType.ERROR) {
    return 'danger'
  }

  return 'default'
}

const renderValidationLabel = (
  value: FormFieldProps['warning'] | FormFieldProps['error'],
  type: ValidationLabelType,
) => {
  return typeof value === 'string' ? (
    <FormFieldValidationText {...{ variant: getVariant(type) }}>
      {value}
    </FormFieldValidationText>
  ) : (
    value
  )
}
export const FormField = memo(
  ({
    children,
    label,
    error,
    info,
    warning,
    labelTooltip,
    ...rest
  }: FormFieldProps) => {
    const { t } = useTranslation()
    const id = useUniqId()
    const labelContent = useMemoizedCall(renderLabel, [
      id,
      label,
      t('optionalLabel'),
    ])
    const errorContent = useMemoizedCall(renderValidationLabel, [
      error,
      ValidationLabelType.ERROR,
    ])
    const infoContent = useMemoizedCall(renderValidationLabel, [
      info,
      ValidationLabelType.INFO,
    ])
    const warningContent = useMemoizedCall(renderValidationLabel, [
      warning,
      ValidationLabelType.WARNING,
    ])
    const showErrorContent = error != null && error !== ''
    const showWarningContent =
      !showErrorContent && warning != null && warning !== ''
    const showInfoContent =
      !showErrorContent && !showWarningContent && info != null && info !== ''

    return (
      <FormFieldContainer {...rest}>
        {labelTooltip ? (
          <FlexBox {...{ ai: 'center', gap: 'xs' }}>
            {labelContent}
            <WrapInToolTip {...{ tooltipText: labelTooltip }}>
              <Icon
                {...{
                  icon: Info,
                  size: 'md',
                  variant: 'blue.100',
                  cursor: 'pointer',
                  mb: 'sm',
                }}
              />
            </WrapInToolTip>
          </FlexBox>
        ) : (
          labelContent
        )}
        {typeof children === 'function' ? children({ id }) : null}
        {typeof children !== 'function'
          ? cloneElement(Children.only(children as any), { id })
          : null}
        {showInfoContent ? infoContent : null}
        {showErrorContent ? errorContent : null}
        {showWarningContent ? warningContent : null}
      </FormFieldContainer>
    )
  },
)
